import { connect } from 'react-redux';
import { Undo2, User, Info, ChevronDown, ChevronRight, X, Save, Loader2, Building2, MapPin, BarChart3, LayoutGrid, FileText, ArrowLeft } from 'lucide-react';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Breadcrumb from '../components/Common/Breadcrumb';
import { getActualColor, getContrast, applyThemePreview, applyThemeFromWhiteLabel } from '../components/utils/ThemeUtils';
import { wlDefaultFonts, POPULAR_GOOGLE_FONTS, deriveThemeTokens } from '../constants/cyfDefaultBranding';
import { Actions as ADMINACTIONS } from '../store/actions/adminActions';
import { Actions as AUTHACTIONS } from '../store/actions/authenticationActions';
import WhiteLabelBrandingAssetUpload from '../components/AdminPortal/WhiteLabelBrandingAssetUpload';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { newSelect as NewSelect } from '../components/ui/newSelect';
import { InputField } from '../components/ui/input-field';
import { SelectField } from '../components/ui/select-field';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Checkbox } from '../components/ui/checkbox';
import BrandShowcase from './BrandShowcase';
import BrandShowcaseWhitelabel from './BrandShowcaseWhitelabel';

const COLOR_GROUPS = [
  {
    label: 'Core Colors',
    fields: [
      { key: 'primaryColor', label: 'Primary Color' },
      { key: 'secondaryColor', label: 'Secondary / Accent' },
      { key: 'bgColor', label: 'Background' },
      { key: 'textColor', label: 'Text Color' },
    ],
  },
  {
    label: 'Buttons',
    fields: [
      { key: 'primaryButton', label: 'Button Color' },
      { key: 'btnTextColor', label: 'Button Text' },
      { key: 'buttonHover', label: 'Button Hover' },
    ],
  },
  {
    label: 'Sidebar',
    fields: [
      { key: 'sidebarBackground', label: 'Background' },
      { key: 'sidebarLinkColor', label: 'Link Color' },
      { key: 'sidebarLinkHover', label: 'Link Hover' },
      { key: 'sidebarLinkFocus', label: 'Link Active' },
    ],
  },
  {
    label: 'Cards & Layout',
    fields: [
      { key: 'cardBackground', label: 'Card Background' },
      { key: 'headerBackground', label: 'Header Background' },
      { key: 'mainLogoBackground', label: 'Logo Background' },
    ],
  },
];

const ADVANCED_GROUPS = [
  {
    label: 'Semantic Colors',
    fields: [
      { key: 'successColor', label: 'Success' },
      { key: 'successBgColor', label: 'Success Background' },
      { key: 'warningColor', label: 'Warning' },
      { key: 'warningBgColor', label: 'Warning Background' },
      { key: 'dangerColor', label: 'Danger' },
      { key: 'dangerBgColor', label: 'Danger Background' },
      { key: 'infoColor', label: 'Info' },
      { key: 'infoBgColor', label: 'Info Background' },
    ],
  },
  {
    label: 'Surfaces & Borders',
    fields: [
      { key: 'mutedColor', label: 'Muted' },
      { key: 'mutedForeground', label: 'Muted Foreground' },
      { key: 'borderColor', label: 'Border' },
      { key: 'popoverBackground', label: 'Popover Background' },
    ],
  },
  {
    label: 'Charts',
    fields: [
      { key: 'chart1', label: 'Chart 1' },
      { key: 'chart2', label: 'Chart 2' },
      { key: 'chart3', label: 'Chart 3' },
      { key: 'chart4', label: 'Chart 4' },
      { key: 'chart5', label: 'Chart 5' },
    ],
  },
  {
    label: 'Misc',
    fields: [
      { key: 'tooltipBackground', label: 'Tooltip Background' },
      { key: 'tooltipText', label: 'Tooltip Text' },
      { key: 'informationBtnBackground', label: 'Info Button' },
      { key: 'menuToggleBackground', label: 'Menu Toggle' },
      { key: 'navbarUserDropdownBg', label: 'User Dropdown Bg' },
    ],
  },
];

const resolveColor = (val) => {
  if (!val) return '#ffffff';
  if (val.startsWith('var(')) return getActualColor(val) || '#ffffff';
  if (val.startsWith('rgba')) return '#888888';
  return val;
};

const ColorField = ({ colorKey, label, value, derived, onChange, onReset }) => {
  const resolved = resolveColor(value || derived);
  const isDerived = !value && derived;

  return (
    <div className='space-y-1.5'>
      <Label className='text-xs font-medium capitalize'>{label}</Label>
      <div className='flex items-center gap-2'>
        <input
          type='color'
          value={resolved}
          onChange={(e) => onChange(colorKey, e.target.value)}
          className='w-9 h-9 cursor-pointer rounded border border-border p-0.5'
        />
        <Input
          value={value || derived || ''}
          onChange={(e) => onChange(colorKey, e.target.value)}
          className='font-mono text-xs h-9'
          placeholder={derived || ''}
        />
        {!isDerived && value && (
          <Button variant='ghost' size='sm' className='h-9 px-2 text-xs' onClick={() => onReset(colorKey)}>
            <Undo2 size={14} strokeWidth={1.5} />
          </Button>
        )}
      </div>
      {isDerived && <p className='text-[10px] text-muted-foreground'>Auto-derived</p>}
    </div>
  );
};

const SidebarPreview = ({ colors, fonts }) => {
  const c = deriveThemeTokens(colors);
  const fontBody = fonts?.body || "'Open Sans', sans-serif";

  return (
    <div className='w-[200px] flex-shrink-0 flex flex-col' style={{ background: c.sidebarBackground || '#1C2B26', fontFamily: fontBody }}>
      <div className='p-4 border-b' style={{ borderColor: c.sidebarBorder || 'rgba(245,240,232,0.15)' }}>
        <div className='text-sm font-semibold' style={{ color: c.sidebarLinkHover || '#F5F0E8', fontFamily: fonts?.headers || fontBody }}>
          Brand Name
        </div>
        <div className='text-[9px] tracking-widest uppercase mt-0.5' style={{ color: c.sidebarLinkFocus || '#8BB89A', fontFamily: fonts?.mono }}>
          Platform
        </div>
      </div>
      <div className='py-3 flex-1'>
        <div className='px-4 pb-1 text-[9px] font-bold tracking-widest uppercase' style={{ color: c.sidebarLinkFocus || '#8BB89A' }}>
          Main
        </div>
        {[
          { Icon: LayoutGrid, label: 'Dashboard', active: true },
          { Icon: Building2, label: 'Organisations' },
          { Icon: MapPin, label: 'Facilities' },
          { Icon: BarChart3, label: 'Usage Data' },
          { Icon: FileText, label: 'Reports' },
        ].map(({ Icon, label, active }) => (
          <div
            key={label}
            className='flex items-center gap-2.5 px-4 py-2 text-xs'
            style={{
              color: active ? (c.sidebarLinkFocus || '#8BB89A') : (c.sidebarLinkColor || 'rgba(245,240,232,0.6)'),
              background: active ? (c.sidebarAccent || 'rgba(139,184,154,0.08)') : 'transparent',
              borderLeft: active ? `2px solid ${c.sidebarLinkFocus || '#8BB89A'}` : '2px solid transparent',
            }}>
            <Icon size={12} strokeWidth={1.5} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
};

const ComponentPreview = ({ colors, fonts, headerLogoUrl }) => {
  const c = deriveThemeTokens(colors);
  const primary = c.primaryColor || c.primaryButton || '#5C8C6E';
  const bg = c.bgColor || '#F5F0E8';
  const card = c.cardBackground || '#FDFCF9';
  const text = c.textColor || '#1C2B26';
  const fontBody = fonts?.body || "'Open Sans', sans-serif";
  const fontHeaders = fonts?.headers || fontBody;
  const fontMono = fonts?.mono || "'Courier New', monospace";

  return (
    <div className='flex-1 overflow-y-auto' style={{ background: bg, color: text, fontFamily: fontBody }}>
      <div className='flex items-center justify-between px-5 py-3 border-b' style={{ background: c.headerBackground || card, borderColor: c.borderColor || 'rgba(45,74,62,0.12)' }}>
        <div className='flex items-center gap-3 min-w-0'>
          {headerLogoUrl ? (
            <img src={headerLogoUrl} alt='' className='h-8 max-w-[120px] object-contain shrink-0' />
          ) : null}
          <div className='text-base font-semibold truncate' style={{ fontFamily: fontHeaders }}>Dashboard</div>
        </div>
        <div className='flex items-center gap-3'>
          <div className='h-7 rounded-full flex items-center gap-2 px-3 text-xs' style={{ background: bg }}>
            <User size={10} strokeWidth={1.5} />
            <span>User</span>
          </div>
          <div className='h-7 w-7 rounded-full flex items-center justify-center text-xs text-white' style={{ background: primary }}>
            <Info size={14} strokeWidth={1.5} />
          </div>
        </div>
      </div>

      <div className='p-5 space-y-5'>
        <div className='grid grid-cols-3 gap-3'>
          {[
            { label: 'Organisations', value: '12', Icon: Building2 },
            { label: 'Facilities', value: '48', Icon: MapPin },
            { label: 'Reports', value: '156', Icon: BarChart3 },
          ].map(({ label, value, Icon }) => (
            <div key={label} className='rounded-lg p-4 border relative overflow-hidden' style={{ background: card, borderColor: c.borderColor || 'rgba(45,74,62,0.12)' }}>
              <div className='absolute top-0 left-0 right-0 h-[3px]' style={{ background: `linear-gradient(90deg, ${primary}, ${c.chart4 || '#8BB89A'})` }} />
              <div className='text-[10px] font-semibold tracking-wider uppercase mb-2' style={{ color: c.mutedForeground || '#4A6359' }}>{label}</div>
              <div className='text-2xl font-bold' style={{ fontFamily: fontHeaders }}>{value}</div>
              <Icon className='absolute right-3 bottom-3 opacity-10' size={20} strokeWidth={1.5} />
            </div>
          ))}
        </div>

        <div className='space-y-2'>
          <div className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Buttons</div>
          <div className='flex flex-wrap gap-2'>
            <Button variant='default' size='sm'>Primary</Button>
            <Button variant='secondary' size='sm'>Secondary</Button>
            <Button variant='outline' size='sm'>Outline</Button>
            <Button variant='destructive' size='sm'>Danger</Button>
            <Button variant='secondary' size='sm' disabled>Disabled</Button>
          </div>
        </div>

        <div className='grid grid-cols-2 gap-3'>
          <div className='space-y-2'>
            <div className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Form Inputs</div>
            <Card className='p-3'>
              <div className='space-y-3'>
                <InputField label='Label' placeholder='Placeholder text' readOnly />
                <SelectField label='Select' placeholder='Choose option...' options={[]} value={null} disabled />
                <div className='flex items-center gap-2'>
                  <Checkbox id='preview-cb' defaultChecked />
                  <Label htmlFor='preview-cb' className='text-xs cursor-pointer'>Checkbox label</Label>
                </div>
              </div>
            </Card>
          </div>

          <div className='space-y-2'>
            <div className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Badges</div>
            <Card className='p-3'>
              <div className='flex flex-wrap gap-1.5'>
                <Badge variant='default'>Default</Badge>
                <Badge variant='success'>Success</Badge>
                <Badge variant='warning'>Warning</Badge>
                <Badge variant='destructive'>Danger</Badge>
                <Badge variant='info'>Info</Badge>
              </div>
              <div className='mt-3 space-y-1.5'>
                <Alert variant='success'>
                  <AlertDescription>Success alert message</AlertDescription>
                </Alert>
                <Alert variant='warning'>
                  <AlertDescription>Warning alert message</AlertDescription>
                </Alert>
                <Alert variant='destructive'>
                  <AlertDescription>Danger alert message</AlertDescription>
                </Alert>
              </div>
            </Card>
          </div>
        </div>

        <div className='space-y-2'>
          <div className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Data Table</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className='text-right'>tCO2e</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[
                { name: 'Electricity', cat: 'Scope 2', val: '24.5' },
                { name: 'Natural Gas', cat: 'Scope 1', val: '12.3' },
                { name: 'Business Travel', cat: 'Scope 3', val: '8.7' },
              ].map(({ name, cat, val }) => (
                <TableRow key={name}>
                  <TableCell className='font-medium'>{name}</TableCell>
                  <TableCell className='text-muted-foreground'>{cat}</TableCell>
                  <TableCell className='text-right text-primary'>{val}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className='grid grid-cols-2 gap-3'>
          <div className='space-y-2'>
            <div className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Tabs</div>
            <Card className='p-3'>
              <Tabs defaultValue='overview' className='w-full'>
                <TabsList className='w-full grid grid-cols-3'>
                  <TabsTrigger value='overview'>Overview</TabsTrigger>
                  <TabsTrigger value='details'>Details</TabsTrigger>
                  <TabsTrigger value='history'>History</TabsTrigger>
                </TabsList>
                <TabsContent value='overview' className='text-xs pt-2'>Tab content placeholder</TabsContent>
                <TabsContent value='details' className='text-xs pt-2'>Details content</TabsContent>
                <TabsContent value='history' className='text-xs pt-2'>History content</TabsContent>
              </Tabs>
            </Card>
          </div>
          <div className='space-y-2'>
            <div className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>Breadcrumbs</div>
            <div className='rounded-lg p-3 border' style={{ background: card, borderColor: c.borderColor || 'rgba(45,74,62,0.12)' }}>
              <div className='flex items-center gap-1.5 text-[10px]'>
                <span style={{ color: primary }}>Home</span>
                <span style={{ color: c.mutedForeground || '#4A6359' }}>/</span>
                <span style={{ color: primary }}>Organisations</span>
                <span style={{ color: c.mutedForeground || '#4A6359' }}>/</span>
                <span style={{ color: c.mutedForeground || '#4A6359' }}>Acme Corp</span>
              </div>
            </div>
          </div>
        </div>

        <div className='space-y-2'>
          <div className='text-xs font-semibold uppercase tracking-wide' style={{ color: c.mutedForeground || '#4A6359' }}>Modal</div>
          <div className='rounded-xl border overflow-hidden max-w-sm' style={{ borderColor: c.borderColor || 'rgba(45,74,62,0.12)', boxShadow: '0 8px 24px rgba(28,43,38,0.12)' }}>
            <div className='px-4 py-3 border-b flex items-center justify-between' style={{ background: card, borderColor: c.borderColor || 'rgba(45,74,62,0.12)' }}>
              <span className='text-sm font-semibold' style={{ fontFamily: fontHeaders }}>Confirm Action</span>
              <div className='w-6 h-6 rounded flex items-center justify-center text-xs cursor-pointer' style={{ background: bg, color: c.mutedForeground || '#4A6359' }}>
                <X size={14} strokeWidth={1.5} />
              </div>
            </div>
            <div className='px-4 py-3 text-xs' style={{ background: card }}>
              Are you sure you want to proceed?
            </div>
            <div className='px-4 py-3 flex justify-end gap-2 border-t' style={{ background: card, borderColor: c.borderColor || 'rgba(45,74,62,0.12)' }}>
              <button className='px-3 py-1 rounded text-xs border' style={{ borderColor: c.borderColor || 'rgba(45,74,62,0.22)', color: text }}>Cancel</button>
              <button className='px-3 py-1 rounded text-xs font-semibold' style={{ background: primary, color: getContrast(primary) }}>Confirm</button>
            </div>
          </div>
        </div>

        <div className='space-y-2'>
          <div className='text-xs font-semibold uppercase tracking-wide' style={{ color: c.mutedForeground || '#4A6359' }}>Tooltip</div>
          <div className='inline-flex flex-col items-center'>
            <div className='px-3 py-1.5 rounded text-xs' style={{ background: c.tooltipBackground || '#1C2B26', color: c.tooltipText || '#F5F0E8' }}>
              Tooltip preview text
            </div>
            <div className='w-2.5 h-2.5 rotate-45 -mt-1' style={{ background: c.tooltipBackground || '#1C2B26' }} />
          </div>
        </div>

        <div className='space-y-2'>
          <div className='text-xs font-semibold uppercase tracking-wide' style={{ color: c.mutedForeground || '#4A6359' }}>Chart Palette</div>
          <div className='flex gap-0 rounded-lg overflow-hidden h-8'>
            {[c.chart1, c.chart2, c.chart3, c.chart4, c.chart5].filter(Boolean).map((color, i) => (
              <div key={i} className='flex-1' style={{ background: color }} />
            ))}
          </div>
        </div>

        <div className='space-y-2'>
          <div className='text-xs font-semibold uppercase tracking-wide' style={{ color: c.mutedForeground || '#4A6359' }}>Typography</div>
          <div className='rounded-lg p-4 border' style={{ background: card, borderColor: c.borderColor || 'rgba(45,74,62,0.12)' }}>
            <div className='text-xl mb-1' style={{ fontFamily: fontHeaders }}>Display Heading</div>
            <div className='text-sm mb-2' style={{ fontFamily: fontBody }}>Body text sample. This demonstrates the body font at normal reading size.</div>
            <div className='text-xs' style={{ fontFamily: fontMono, color: c.mutedForeground || '#4A6359' }}>
              tCO2e: 48.234 -- Scope 1: 12.4 -- Scope 2: 35.8
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const buildAssetPreviewFromPending = (pending) => {
  const o = {};
  ['logo', 'favicon', 'logoEmail'].forEach((k) => {
    if (pending[k] !== undefined) o[k] = pending[k];
  });
  return Object.keys(o).length ? o : null;
};

const Theming = ({
  branding,
  whiteLabelAdmin,
  saveWLSettings,
  isWhiteLabelApp,
  presignWhiteLabelBranding,
  replaceBrandingAssetPreview,
  clearBrandingAssetPreview,
  getWLSettings,
}) => {
  const { logo, appTopLeftLogo } = branding || {};
  const savedColors = branding?.colors || {};
  const savedColorsFromBranding = branding?.colors;
  const savedFontConfig = Array.isArray(branding?.fonts) ? branding.fonts[0] : branding?.fonts;

  const [colorOverrides, setColorOverrides] = useState({});
  const [fontState, setFontState] = useState({
    body: savedFontConfig?.font?.body || "'Open Sans', sans-serif",
    headers: savedFontConfig?.font?.headers || "'Open Sans', sans-serif",
    mono: savedFontConfig?.font?.mono || "'Courier New', monospace",
    googleFontsUrl: savedFontConfig?.googleFontsUrl || '',
    customFontCss: savedFontConfig?.customFontCss || '',
  });
  const [activeTab, setActiveTab] = useState('edit');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [pendingAssets, setPendingAssets] = useState({});
  const wlId = whiteLabelAdmin?.whiteLabelId;
  const canUploadBranding = wlId && String(wlId) !== '0';

  const setAssetPending = useCallback((key, val) => {
    setPendingAssets((prev) => {
      const next = { ...prev };
      if (val === undefined) delete next[key];
      else next[key] = val;
      return next;
    });
  }, []);

  useEffect(() => {
    replaceBrandingAssetPreview(buildAssetPreviewFromPending(pendingAssets));
  }, [pendingAssets, replaceBrandingAssetPreview]);

  useEffect(
    () => () => {
      clearBrandingAssetPreview();
    },
    [clearBrandingAssetPreview],
  );

  const previewHeaderLogo =
    pendingAssets.logo !== undefined
      ? pendingAssets.logo || appTopLeftLogo || logo
      : appTopLeftLogo || logo;

  useEffect(() => {
    if (savedColors && Object.keys(savedColors).length) {
      setColorOverrides({ ...savedColors });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync once on mount; branding updates after save handled by reload/navigation
  }, []);

  useEffect(() => {
    if (savedFontConfig) {
      setFontState({
        body: savedFontConfig?.font?.body || "'Open Sans', sans-serif",
        headers: savedFontConfig?.font?.headers || "'Open Sans', sans-serif",
        mono: savedFontConfig?.font?.mono || "'Courier New', monospace",
        googleFontsUrl: savedFontConfig?.googleFontsUrl || '',
        customFontCss: savedFontConfig?.customFontCss || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFontConfig?.font?.body]);

  const derivedColors = useMemo(() => deriveThemeTokens(colorOverrides), [colorOverrides]);

  const handleColorChange = useCallback((key, value) => {
    setColorOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleResetField = useCallback((key) => {
    setColorOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const handleFontChange = useCallback((key, value) => {
    setFontState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handlePopularFont = useCallback((option) => {
    if (option == null) return;
    const val = typeof option === 'object' && option !== null ? option.value : option;
    const selected = POPULAR_GOOGLE_FONTS.find((f) => f.value === val);
    if (selected) {
      setFontState((prev) => ({
        ...prev,
        body: `'${selected.value}', sans-serif`,
        headers: `'${selected.value}', serif`,
        mono: `'${selected.value}', monospace`,
        googleFontsUrl: selected.url,
      }));
    }
  }, []);

  const handleReset = useCallback(() => {
    const nextColors =
      savedColorsFromBranding && Object.keys(savedColorsFromBranding).length
        ? { ...savedColorsFromBranding }
        : {};
    const config = savedFontConfig || wlDefaultFonts[0];
    const nextFontState = {
      body: config?.font?.body || "'Open Sans', sans-serif",
      headers: config?.font?.headers || "'Open Sans', sans-serif",
      mono: config?.font?.mono || "'Courier New', monospace",
      googleFontsUrl: config?.googleFontsUrl || '',
      customFontCss: config?.customFontCss || '',
    };
    setColorOverrides(nextColors);
    setFontState(nextFontState);
    setPendingAssets({});
    clearBrandingAssetPreview();
    applyThemePreview(nextColors, nextFontState);
  }, [savedColorsFromBranding, savedFontConfig, clearBrandingAssetPreview]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const settings = {
        ...whiteLabelAdmin,
        brandingColors: { ...derivedColors, ...colorOverrides },
        font: [
          {
            font: { body: fontState.body, headers: fontState.headers, mono: fontState.mono },
            googleFontsUrl: fontState.googleFontsUrl || null,
            customFontCss: fontState.customFontCss || null,
            size: savedFontConfig?.size || ['2.3em', '2em', '1.1em'],
            weight: savedFontConfig?.weight || [300, 400, 500, 600, 700, 900],
            style: savedFontConfig?.style || 'italic',
          },
        ],
      };
      if (pendingAssets.logo !== undefined) settings.logo = pendingAssets.logo;
      if (pendingAssets.favicon !== undefined) settings.favicon = pendingAssets.favicon;
      if (pendingAssets.logoEmail !== undefined) settings.logoEmail = pendingAssets.logoEmail;
      await saveWLSettings(settings);
      applyThemeFromWhiteLabel(settings);
      setPendingAssets({});
      clearBrandingAssetPreview();
      await getWLSettings({ bypassCache: true });
    } catch {
      /* saveWLSettings already toasts */
    } finally {
      setSaving(false);
    }
  }, [
    whiteLabelAdmin,
    colorOverrides,
    derivedColors,
    fontState,
    savedFontConfig,
    saveWLSettings,
    pendingAssets,
    clearBrandingAssetPreview,
    getWLSettings,
  ]);

  useEffect(() => {
    if (activeTab === 'preview') {
      applyThemePreview(colorOverrides, fontState);
    }
  }, [activeTab, colorOverrides, fontState]);

  const popularFontOptions = POPULAR_GOOGLE_FONTS.map((f) => ({ label: f.label, value: f.value }));

  const currentPopularFontValue =
    fontState.googleFontsUrl && POPULAR_GOOGLE_FONTS.find((f) => f.url === fontState.googleFontsUrl)?.value;

  return (
    <React.Fragment>
      <Breadcrumb items={[{ label: 'Theme Settings', link: '/theming' }]} />
      <div className='container mx-auto py-6 px-4'>
        <Card className='bg-card border-border'>
          <CardHeader className='flex flex-row items-center justify-between border-b border-border'>
            <CardTitle className='text-xl font-semibold font-display'>Theme Settings</CardTitle>
            <div className='flex gap-2'>
              <Button variant='outline' size='sm' onClick={handleReset} disabled={saving}>
                <Undo2 size={14} strokeWidth={1.5} className='mr-2' /> Reset
              </Button>
              <Button size='sm' onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 size={14} strokeWidth={1.5} className='animate-spin mr-2' /> : <Save size={14} strokeWidth={1.5} className='mr-2' />}
                Save Theme
              </Button>
            </div>
          </CardHeader>
          <CardContent className='p-0'>
            <Tabs value={activeTab} onValueChange={setActiveTab} className='w-full'>
              <div className='px-6 pt-4'>
                <TabsList className='grid w-full max-w-md grid-cols-2'>
                  <TabsTrigger value='edit'>Edit Theme</TabsTrigger>
                  <TabsTrigger value='preview'>Live Preview</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value='edit' className='p-6 space-y-8'>
                {COLOR_GROUPS.map((group) => (
                  <div key={group.label}>
                    <h3 className='text-sm font-semibold mb-3 pb-2 border-b border-border'>{group.label}</h3>
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                      {group.fields.map((field) => (
                        <ColorField
                          key={field.key}
                          colorKey={field.key}
                          label={field.label}
                          value={colorOverrides[field.key]}
                          derived={derivedColors[field.key]}
                          onChange={handleColorChange}
                          onReset={handleResetField}
                        />
                      ))}
                    </div>
                  </div>
                ))}

                <div>
                  <h3 className='text-sm font-semibold mb-3 pb-2 border-b border-border'>Typography</h3>
                  <p className='text-xs text-muted-foreground mb-4'>
                    To use custom fonts, either pick one from <strong>Popular Fonts</strong> (sets the URL for you) or paste a <strong>Google Fonts URL</strong> below. The font will load site-wide once the URL is set and you save.
                  </p>
                  <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                    <div className='space-y-1.5'>
                      <Label className='text-xs font-medium'>Popular Fonts</Label>
                      <NewSelect
                        options={popularFontOptions}
                        value={currentPopularFontValue ?? null}
                        onChange={handlePopularFont}
                        placeholder='Quick pick a font...'
                        isClearable
                        searchable
                      />
                      <p className='text-[11px] text-muted-foreground'>
                        Choose a font to apply to body, headers and mono, and set the Google Fonts URL automatically.
                      </p>
                    </div>
                    <div className='space-y-1.5'>
                      <Label className='text-xs font-medium'>Body Font Family</Label>
                      <Input value={fontState.body} onChange={(e) => handleFontChange('body', e.target.value)} className='font-mono text-xs' placeholder="e.g. 'Open Sans', sans-serif" />
                    </div>
                    <div className='space-y-1.5'>
                      <Label className='text-xs font-medium'>Display / Header Font</Label>
                      <Input value={fontState.headers} onChange={(e) => handleFontChange('headers', e.target.value)} className='font-mono text-xs' placeholder="e.g. 'Open Sans', serif" />
                    </div>
                    <div className='space-y-1.5'>
                      <Label className='text-xs font-medium'>Mono Font</Label>
                      <Input value={fontState.mono} onChange={(e) => handleFontChange('mono', e.target.value)} className='font-mono text-xs' placeholder="e.g. 'Courier New', monospace" />
                    </div>
                    <div className='space-y-1.5 md:col-span-2'>
                      <Label className='text-xs font-medium'>Google Fonts URL</Label>
                      <Input value={fontState.googleFontsUrl} onChange={(e) => handleFontChange('googleFontsUrl', e.target.value)} className='font-mono text-xs' placeholder='https://fonts.googleapis.com/css2?family=...' />
                      <p className='text-[11px] text-muted-foreground'>
                        Paste the CSS link from fonts.google.com: choose a font → Get font → copy the &lt;link&gt; href or “Import” URL. Required for custom fonts to appear.
                      </p>
                    </div>
                    <div className='space-y-1.5 lg:col-span-3'>
                      <Label className='text-xs font-medium'>Custom Font CSS (Advanced)</Label>
                      <textarea
                        value={fontState.customFontCss}
                        onChange={(e) => handleFontChange('customFontCss', e.target.value)}
                        className='w-full min-h-[80px] px-3 py-2 rounded-md border border-border bg-card font-mono text-xs resize-y'
                        placeholder={'@font-face {\n  font-family: "MyFont";\n  src: url("...") format("woff2");\n}'}
                      />
                      <p className='text-[11px] text-muted-foreground'>
                        For self-hosted fonts, add @font-face rules here. Reference the font in the family fields above (e.g. font-family: "MyFont").
                      </p>
                    </div>
                  </div>
                </div>

                <div className='space-y-4'>
                  <div>
                    <h3 className='text-sm font-semibold mb-2 pb-2 border-b border-border'>Logos and icons</h3>
                    <p className='text-xs text-muted-foreground mb-4'>
                      Upload files for temporary preview in the app until you save. Saving copies them to permanent URLs. If save fails with a copy error, the preview may have expired—upload again.
                      Email logo: use PNG or JPEG; SVG is not supported for email.
                    </p>
                    {!canUploadBranding && (
                      <Alert className='mb-4 border-border bg-muted/50'>
                        <AlertDescription className='text-muted-foreground'>
                          Branding uploads require a white-label organisation context (valid whiteLabelId). CYF admins can upload logos for any white-label from Admin → Settings (select the white-label first).
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className='grid gap-4 max-w-3xl'>
                      <WhiteLabelBrandingAssetUpload
                        assetType='logo'
                        label='App logo'
                        description='Shown in the header. SVG, PNG, JPEG, or WebP.'
                        whiteLabelId={wlId}
                        savedUrl={logo}
                        pendingValue={pendingAssets.logo}
                        onPendingChange={(v) => setAssetPending('logo', v)}
                        presignWhiteLabelBranding={presignWhiteLabelBranding}
                        disabled={!canUploadBranding || saving}
                      />
                      <WhiteLabelBrandingAssetUpload
                        assetType='favicon'
                        label='Favicon'
                        description='Browser tab icon. ICO, SVG, PNG, JPEG, or WebP.'
                        whiteLabelId={wlId}
                        savedUrl={branding?.favicon}
                        pendingValue={pendingAssets.favicon}
                        onPendingChange={(v) => setAssetPending('favicon', v)}
                        presignWhiteLabelBranding={presignWhiteLabelBranding}
                        disabled={!canUploadBranding || saving}
                      />
                      <WhiteLabelBrandingAssetUpload
                        assetType='logoEmail'
                        label='Email logo'
                        description='Raster or ICO only (no SVG). Shown in branded emails.'
                        whiteLabelId={wlId}
                        savedUrl={whiteLabelAdmin?.logoEmail}
                        pendingValue={pendingAssets.logoEmail}
                        onPendingChange={(v) => setAssetPending('logoEmail', v)}
                        presignWhiteLabelBranding={presignWhiteLabelBranding}
                        disabled={!canUploadBranding || saving}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <button
                    type='button'
                    className='flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors'
                    onClick={() => setShowAdvanced((v) => !v)}>
                    {showAdvanced ? <ChevronDown size={14} strokeWidth={1.5} /> : <ChevronRight size={14} strokeWidth={1.5} />}
                    Advanced Options
                  </button>

                  {showAdvanced && (
                    <div className='mt-4 space-y-6 pl-4 border-l-2 border-border'>
                      {ADVANCED_GROUPS.map((group) => (
                        <div key={group.label}>
                          <h4 className='text-xs font-semibold mb-3 text-muted-foreground uppercase tracking-wider'>{group.label}</h4>
                          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                            {group.fields.map((field) => (
                              <ColorField
                                key={field.key}
                                colorKey={field.key}
                                label={field.label}
                                value={colorOverrides[field.key]}
                                derived={derivedColors[field.key]}
                                onChange={handleColorChange}
                                onReset={handleResetField}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value='preview' className='border-t border-border'>
                <div className='px-6 py-3 flex items-center justify-between border-b border-border bg-muted/30'>
                  <span className='text-sm text-muted-foreground'>Preview uses your current theme settings.</span>
                  <Button
                    type='button'
                    variant='outline'
                    size='sm'
                    onClick={() => setShowFullPreview(true)}
                    className='inline-flex items-center gap-2'>
                    Open full component preview
                  </Button>
                </div>
                <div className='flex' style={{ height: '700px' }}>
                  <SidebarPreview colors={colorOverrides} fonts={fontState} />
                  <ComponentPreview colors={colorOverrides} fonts={fontState} headerLogoUrl={previewHeaderLogo} />
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {showFullPreview && (
        <div className='fixed inset-0 z-[100] bg-background overflow-auto'>
          <div className='sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-background border-b border-border shadow-sm'>
            <Button type='button' variant='outline' size='sm' onClick={() => setShowFullPreview(false)} className='gap-2'>
              <ArrowLeft size={16} strokeWidth={1.5} />
              Back to theme settings
            </Button>
          </div>
          <div className='min-h-screen'>
            {whiteLabelAdmin || isWhiteLabelApp ? <BrandShowcaseWhitelabel /> : <BrandShowcase />}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({
  branding: state?.branding,
  whiteLabelAdmin: state?.whiteLabelAdmin,
  isWhiteLabelApp: state?.isWhiteLabelApp,
});

const mapDispatchToProps = (dispatch) => ({
  saveWLSettings: (settings) => dispatch(ADMINACTIONS.saveWLSettings(settings)),
  presignWhiteLabelBranding: (body) => dispatch(ADMINACTIONS.presignWhiteLabelBranding(body)),
  replaceBrandingAssetPreview: (p) => dispatch(ADMINACTIONS.replaceBrandingAssetPreview(p)),
  clearBrandingAssetPreview: () => dispatch(ADMINACTIONS.clearBrandingAssetPreview()),
  getWLSettings: (options) => dispatch(AUTHACTIONS.getWLSettings(options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Theming);
